import { useDocumentEvent } from '../../../../lib/browser/useEventListener';
import type { MediaController } from './media/useMediaController';

const TYPING_TARGETS = new Set(['TEXTAREA', 'INPUT', 'SELECT']);
const PLAY_PAUSE_KEY = ' ';
const CAPTURE_KEY = 'c';
const FULL_VIEW_KEY = 'f';
const STEP_BACK_KEY = 'ArrowLeft';
const STEP_FORWARD_KEY = 'ArrowRight';
const ESCAPE_KEY = 'Escape';

/** Space and ← / → belong to the player only while nothing else owns them. */
const TRANSPORT_KEYS = new Set<string>([PLAY_PAUSE_KEY, STEP_BACK_KEY, STEP_FORWARD_KEY]);

/**
 * Controls that answer to Space or the arrows themselves: buttons and links
 * activate on Space, radiogroups and tab lists move the selection with the
 * arrows, and the scrubber and the column splitters nudge with them. The map
 * is document-wide, so without this it would preventDefault the whole panel's
 * keyboard operation out from under those controls.
 */
const INTERACTIVE_TARGET_SELECTOR =
  'button, a[href], [role="radio"], [role="slider"], [role="separator"], [role="tab"], [role="menuitem"], [role="button"], [role="checkbox"], [role="switch"], [role="menuitemcheckbox"], [role="menuitemradio"], [contenteditable="true"]';

/**
 * Popup lists own the letter keys (typeahead): a "c" typed into an open
 * select, menu or combobox picks an item, it does not start a capture.
 */
const TYPEAHEAD_TARGET_SELECTOR = '[role="listbox"], [role="menu"], [role="option"], [role="combobox"], [role="menuitem"]';

/** Any dialog layer; Radix keeps a closing one mounted until its exit animation ends. */
const MODAL_LAYER_SELECTOR = '[role="dialog"]:not([data-state="closed"]), [role="alertdialog"]:not([data-state="closed"])';

/** One frame back (-1) or forward (+1). */
export type StepDirection = -1 | 1;

/** A stack of "something is open" handlers; each returns true when it consumed Esc. */
export type EscapeHandler = () => boolean;

/** What the shortcuts act on — read when a key arrives, so they always see the latest. */
interface ShortcutTargets {
  controller: MediaController;
  onCapture: () => void;
  /** ← / → step the playhead one frame. */
  onStep: (direction: StepDirection) => void;
  /** F toggles the full-view modal. */
  onFullView: () => void;
  /**
   * A frame is being marked up: Space and ← / → do nothing, since moving the
   * video would leave the markup over a frame it no longer shows.
   */
  transportFrozen: boolean;
  /** Handlers are tried in order (innermost first); the first that returns true wins. */
  escapeStack: EscapeHandler[];
}

export interface UseReviewShortcutsArgs extends ShortcutTargets {
  enabled: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (TYPING_TARGETS.has(target.tagName)) return true;
  return target.isContentEditable === true;
}

/** True when the key press landed on (or inside) a control that owns it. */
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest(INTERACTIVE_TARGET_SELECTOR) !== null;
}

function isTypeaheadTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest(TYPEAHEAD_TARGET_SELECTOR) !== null;
}

/**
 * True while a dialog the player does not live in is open (a confirm raised
 * by the chat, a menu's dialog, ...). The player's own layers — the sheet and
 * the full-view layer — contain the <video>, so they never count.
 */
function foreignModalOpen(controller: MediaController): boolean {
  const video = controller.element;
  if (!video) return false;
  for (const layer of document.querySelectorAll(MODAL_LAYER_SELECTOR)) {
    if (!layer.contains(video)) return true;
  }
  return false;
}

function hasModifier(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey || e.altKey;
}

function stepDirectionFor(key: string): StepDirection {
  if (key === STEP_BACK_KEY) return -1;
  return 1;
}

/** Walk the escape stack; true when a handler consumed the key. */
export function runEscapeStack(stack: EscapeHandler[]): boolean {
  for (const handler of stack) {
    if (handler()) return true;
  }
  return false;
}

/** Whether the escape stack consumed each Escape keypress it has already seen. */
const escapeOutcomes = new WeakMap<KeyboardEvent, boolean>();

/**
 * Runs the escape stack once per Escape keypress. Two listeners route the
 * same keypress through it — this hook's and the Radix Sheet's
 * `onEscapeKeyDown` — and which of them runs first depends on mount order
 * (Radix's Portal mounts a render late), so neither can rely on the other
 * having run. Whichever comes second gets the first one's answer instead of
 * popping a second layer.
 */
export function runEscapeOnce(event: KeyboardEvent, run: () => boolean): boolean {
  const known = escapeOutcomes.get(event);
  if (known !== undefined) return known;
  const consumed = run();
  escapeOutcomes.set(event, consumed);
  return consumed;
}

function handleEscape(e: KeyboardEvent, escapeStack: EscapeHandler[]): void {
  if (!runEscapeOnce(e, () => runEscapeStack(escapeStack))) return;
  e.preventDefault();
  e.stopPropagation();
}

function togglePlayback(controller: MediaController): void {
  if (controller.paused) controller.play();
  else controller.pause();
}

function handleTransportKey(e: KeyboardEvent, targets: ShortcutTargets): void {
  // The focused control answers to this key itself — leave it alone, and
  // leave it cancellable so the control's own handler still runs.
  if (isInteractiveTarget(e.target)) return;

  e.preventDefault();
  if (e.key === PLAY_PAUSE_KEY) {
    togglePlayback(targets.controller);
    return;
  }
  targets.onStep(stepDirectionFor(e.key));
}

function handleLetterKey(e: KeyboardEvent, targets: ShortcutTargets): void {
  if (isTypeaheadTarget(e.target)) return;
  const lowered = e.key.toLowerCase();
  if (lowered === CAPTURE_KEY) {
    e.preventDefault();
    targets.onCapture();
    return;
  }
  if (lowered === FULL_VIEW_KEY) {
    e.preventDefault();
    targets.onFullView();
  }
}

function handleShortcut(e: KeyboardEvent, targets: ShortcutTargets): void {
  if (foreignModalOpen(targets.controller)) return;
  if (e.key === ESCAPE_KEY) {
    handleEscape(e, targets.escapeStack);
    return;
  }
  if (isTypingTarget(e.target) || hasModifier(e)) return;
  if (TRANSPORT_KEYS.has(e.key)) {
    if (!targets.transportFrozen) handleTransportKey(e, targets);
    return;
  }
  handleLetterKey(e, targets);
}

/**
 * Keyboard shortcuts for the review theater: Space toggles play/pause, C
 * captures, ← / → step a frame, F toggles full view, Esc walks the escape
 * stack. Ignored while typing (except Esc, which still closes what is open)
 * and when a modifier is held, so Ctrl+C still copies.
 *
 * Everything, Esc included, stands down while a dialog outside the player is
 * open — its keys are its own. Space and ← / → additionally stand down for
 * whatever control has focus — see `INTERACTIVE_TARGET_SELECTOR` — and while a
 * frame is being marked up. C and F keep working wherever focus happens to sit,
 * except inside an open select or menu, where letters are typeahead.
 */
export function useReviewShortcuts({ enabled, ...targets }: UseReviewShortcutsArgs): void {
  function onKeyDown(e: KeyboardEvent): void {
    handleShortcut(e, targets);
  }

  // Capture phase so this fires ahead of any other document-level listener
  // (e.g. a focused control's own key handling). Escape is shared with the
  // Radix Sheet's `onEscapeKeyDown` through `runEscapeOnce`, so the order the
  // two listeners run in doesn't matter.
  useDocumentEvent('keydown', onKeyDown, { enabled, capture: true });
}
